import type {
  AjaxResponseEnvelope,
  DownloadCheckoutRequest,
  DownloadPurchaseResult,
  PaypalAuthorizeData,
  PaypalCheckoutStartData,
  PaypalSdk,
} from '@Components/checkout-flow/checkout-flow.types';
import {AJAX_STATUS_ERROR, AJAX_STATUS_SUCCESS, PAYPAL_CHECKOUT_SCRIPT_URL, PAYPAL_ERROR_LANG_KEY_BY_CODE} from '@Components/checkout-flow/checkout-flow.types';
import {openMessageModal} from '@Modals/message-modal';
import {noop} from '@Utils/general.util';

let paypalSdkPromise: Promise<PaypalSdk> | null = null;

const getPaypalSdkFromWindow = (): PaypalSdk | null => {
  const candidate = (window as unknown as {paypal?: PaypalSdk}).paypal;
  return candidate ?? null;
};

export const loadPaypalSdk = (): Promise<PaypalSdk> => {
  if (paypalSdkPromise !== null) {
    return paypalSdkPromise;
  }
  const alreadyLoaded = getPaypalSdkFromWindow();
  if (alreadyLoaded !== null) {
    paypalSdkPromise = Promise.resolve(alreadyLoaded);
    return paypalSdkPromise;
  }

  paypalSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PAYPAL_CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.onload = (): void => {
      const sdk = getPaypalSdkFromWindow();
      if (sdk === null) {
        paypalSdkPromise = null;
        reject(new Error('PayPal SDK did not initialise'));
        return;
      }
      resolve(sdk);
    };
    script.onerror = (): void => {
      paypalSdkPromise = null;
      reject(new Error('Unable to load the PayPal SDK'));
    };
    document.head.appendChild(script);
  });
  return paypalSdkPromise;
};

export interface RenderPaypalButtonOptions {
  environment: string;
  containerSelector: string;
  request: DownloadCheckoutRequest;
  getBillingAddressId: () => number | null;
  onPurchaseCompleted: (result: DownloadPurchaseResult) => void;
}

export const renderPaypalButton = async ({environment, containerSelector, request, getBillingAddressId, onPurchaseCompleted}: RenderPaypalButtonOptions): Promise<void> => {
  const sdk = await loadPaypalSdk();
  let paypalAccount = '';
  let cartId = request.cartId;
  let isErrorAlreadyShown = false;

  sdk.Button.render(
    {
      env: environment,
      commit: false,
      style: {size: 'responsive', height: 48, color: 'gold', shape: 'rect', label: 'checkout', tagline: false},
      payment: async (): Promise<string | undefined> => {
        const response = (await window.PMW.write(window.PMW.util.site_url('cart/checkoutPayPalDownload'), {
          id: cartId,
          idBillingAddress: getBillingAddressId(),
        })) as AjaxResponseEnvelope<PaypalCheckoutStartData>;

        if (response.status === AJAX_STATUS_SUCCESS) {
          paypalAccount = response.data.account;
          cartId = response.data.cartId;
          return response.data.id;
        }
        if (response.status === AJAX_STATUS_ERROR) {
          isErrorAlreadyShown = true;
          showPaypalError(response.code);
        }
        return undefined;
      },
      onAuthorize: async (data: PaypalAuthorizeData): Promise<void> => {
        const response = (await window.PMW.write(window.PMW.util.site_url('cart/executePaypalPayment'), {
          account: paypalAccount,
          paymentId: data.paymentID,
          payerId: data.payerID,
          cartId,
          billing: getBillingAddressId(),
        })) as AjaxResponseEnvelope<DownloadPurchaseResult>;

        if (response.status !== AJAX_STATUS_SUCCESS) {
          showPaypalError(response.code);
          return;
        }
        onPurchaseCompleted(response.data);
      },
      onCancel: noop,
      onError: (error: unknown): void => {
        if (isErrorAlreadyShown) {
          isErrorAlreadyShown = false;
          return;
        }
        showPaypalError(undefined);
        window.PMW.log(`Error during PayPal checkout: ${String(error)}`);
      },
    },
    containerSelector
  );
};

const showPaypalError = (code: string | undefined): void => {
  const langKey = code !== undefined ? PAYPAL_ERROR_LANG_KEY_BY_CODE[code] : undefined;
  openMessageModal({text: window.i18next.t(langKey ?? 'pmwjs_error_reload_page')});
};
